import React, { useState, useEffect, useRef } from "react";
import api from "../api";

/**
 * CourseOverviewPanel
 * A slide-in drawer showing the full course tree (TopicHeader → Chapter → Lessons).
 *
 * Props:
 *  isOpen           boolean
 *  onClose          () => void
 *  selectedCourse   course object
 *  selectedLesson   currently active lesson
 *  onSelectLesson   (lessonMeta) => void
 *  onSelectWorkspace (view: 'cover' | 'courseData') => void
 *  editorView       'lesson' | 'cover' | 'courseData'
 *  hasUnsavedChanges boolean
 *  regeneratingLessons Map<sectionNumber, { status }>
 *  lessonDrafts     { [sectionNumber]: { status: "queued" | "editing" | "unsaved" | "error", lesson?, message? } }
 *  pendingCoverCount number of unpublished cover options still in S3
 */
const CourseOverviewPanel = ({
  isOpen,
  onClose,
  selectedCourse,
  selectedLesson,
  onSelectLesson,
  onSelectWorkspace,
  editorView = "lesson",
  hasUnsavedChanges = false,
  regeneratingLessons = new Map(),
  lessonDrafts = {},
  pendingCoverCount = 0,
}) => {
  const [allLessons, setAllLessons] = useState([]);
  const [collapsedHeaders, setCollapsedHeaders] = useState({});
  const [collapsedChapters, setCollapsedChapters] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const drawerRef = useRef(null);
  const bodyRef = useRef(null);
  const activeLessonRef = useRef(null);

  useEffect(() => {
    if (isOpen && selectedCourse) {
      fetchAllLessons();
    }
  }, [isOpen, selectedCourse]);

  const fetchAllLessons = async () => {
    if (!selectedCourse) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/${selectedCourse.courseTitle}/allLessons`, {
        params: {
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      setAllLessons(response.data || []);
    } catch (err) {
      console.error("Error fetching all lessons:", err);
      setError("Failed to load course lessons.");
    } finally {
      setLoading(false);
    }
  };

  // Group by topicHeader then by topicNumber
  const groupedTree = () => {
    const tree = {};
    for (const lesson of allLessons) {
      const headerKey = lesson.topicHeaderNumber || "0";
      const chapterKey = lesson.topicNumber || "0";
      if (!tree[headerKey]) {
        tree[headerKey] = {
          label: lesson.topicHeader || "General",
          headerNumber: lesson.topicHeaderNumber,
          chapters: {},
        };
      }
      if (!tree[headerKey].chapters[chapterKey]) {
        tree[headerKey].chapters[chapterKey] = {
          label: lesson.topicTitle || "",
          topicNumber: lesson.topicNumber,
          lessons: [],
        };
      }
      tree[headerKey].chapters[chapterKey].lessons.push(lesson);
    }
    return tree;
  };

  const toggleHeader = (key) =>
    setCollapsedHeaders((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleChapter = (key) =>
    setCollapsedChapters((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedSectionNumber = selectedLesson?.sectionNumber
    ? String(selectedLesson.sectionNumber)
    : "";

  const selectedLessonMeta = allLessons.find(
    (item) => String(item.sectionNumber) === selectedSectionNumber
  );
  const selectedHeaderKey = selectedLessonMeta
    ? selectedLessonMeta.topicHeaderNumber || "0"
    : "";
  const selectedChapterKey = selectedLessonMeta
    ? selectedLessonMeta.topicNumber || "0"
    : "";

  // Expand the selected lesson's topic and chapter when the drawer opens or reloads.
  useEffect(() => {
    if (!isOpen || !selectedHeaderKey || !selectedChapterKey) return;
    setCollapsedHeaders((prev) =>
      prev[selectedHeaderKey] ? { ...prev, [selectedHeaderKey]: false } : prev
    );
    setCollapsedChapters((prev) =>
      prev[selectedChapterKey] ? { ...prev, [selectedChapterKey]: false } : prev
    );
  }, [isOpen, selectedHeaderKey, selectedChapterKey]);

  // Scroll the open drawer to the selected lesson after the tree has rendered.
  useEffect(() => {
    if (!isOpen || loading || !selectedSectionNumber) return;

    const scrollToSelected = () => {
      const el = activeLessonRef.current;
      const body = bodyRef.current;
      if (!el || !body) return;
      const bodyRect = body.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const target =
        body.scrollTop +
        (elRect.top - bodyRect.top) -
        body.clientHeight / 2 +
        elRect.height / 2;
      body.scrollTo({ top: Math.max(0, target), behavior: "auto" });
    };

    const timer = window.setTimeout(scrollToSelected, 50);
    return () => window.clearTimeout(timer);
  }, [isOpen, loading, selectedSectionNumber, allLessons.length]);

  const handleLessonClick = (lesson) => {
    const isCurrentLesson =
      editorView === "lesson" &&
      selectedLesson &&
      selectedLesson.sectionNumber === lesson.sectionNumber;
    if (isCurrentLesson) return;

    // A drafted lesson's edits are mirrored into its draft, so leaving it loses nothing.
    const currentDrafted =
      selectedLesson &&
      lessonDrafts[String(selectedLesson.sectionNumber)]?.status === "unsaved";
    if (hasUnsavedChanges && !currentDrafted) {
      const confirmed = window.confirm(
        "You have unsaved changes. Switch lessons anyway?\n\nUnsaved changes will be lost."
      );
      if (!confirmed) return;
    }

    onSelectLesson(lesson);
    onClose();
  };

  const handleWorkspaceClick = (view) => {
    if (editorView === view) {
      onClose();
      return;
    }
    if (typeof onSelectWorkspace === "function") onSelectWorkspace(view);
    onClose();
  };

  const tree = groupedTree();

  const draftedNumbers = new Set(
    Object.entries(lessonDrafts)
      .filter(([, draft]) => draft?.status === "unsaved")
      .map(([number]) => number)
  );
  if (editorView === "lesson" && hasUnsavedChanges && selectedSectionNumber) {
    draftedNumbers.add(selectedSectionNumber);
  }
  const unsavedCount = draftedNumbers.size;

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (isOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`course-overview-backdrop ${isOpen ? "open" : ""}`}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`course-overview-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-label="Course Overview"
      >
        <div className="course-overview-header">
          <h2 className="course-overview-title">
            {selectedCourse ? selectedCourse.courseTitle : "Course Overview"}
          </h2>
          <button
            className="course-overview-close"
            onClick={onClose}
            aria-label="Close course overview"
          >
            ✕
          </button>
        </div>

        <div className="course-overview-body" ref={bodyRef}>
          {loading && (
            <div className="course-overview-loading">Loading lessons…</div>
          )}
          {error && <div className="course-overview-error">{error}</div>}

          {selectedCourse && (
            <div className="course-tree-pinned">
              <button
                className={`course-tree-lesson-row course-tree-workspace-row ${
                  editorView === "cover" ? "active" : ""
                }`}
                onClick={() => handleWorkspaceClick("cover")}
              >
                <span className="lesson-row-number">Cover</span>
                <span className="lesson-row-title">Course cover</span>
                {pendingCoverCount > 0 && (
                  <span className="lesson-row-badges">
                    <span className="lesson-badge pending-cover">Pick one</span>
                  </span>
                )}
              </button>
              <button
                className={`course-tree-lesson-row course-tree-workspace-row ${
                  editorView === "courseData" ? "active" : ""
                }`}
                onClick={() => handleWorkspaceClick("courseData")}
              >
                <span className="lesson-row-number">Data</span>
                <span className="lesson-row-title">Course description</span>
              </button>
            </div>
          )}

          {!loading && !error && Object.entries(tree).map(([headerKey, headerData]) => (
            <div key={headerKey} className="course-tree-header-group">
              {/* Topic Header */}
              <button
                className="course-tree-header-toggle"
                onClick={() => toggleHeader(headerKey)}
              >
                <span className="course-tree-chevron">
                  {collapsedHeaders[headerKey] ? "▶" : "▼"}
                </span>
                <span className="course-tree-header-label">
                  {headerData.headerNumber}. {headerData.label}
                </span>
              </button>

              {!collapsedHeaders[headerKey] && (
                <div className="course-tree-chapters">
                  {Object.entries(headerData.chapters).map(([chapterKey, chapterData]) => (
                    <div key={chapterKey} className="course-tree-chapter-group">
                      {/* Chapter */}
                      <button
                        className="course-tree-chapter-toggle"
                        onClick={() => toggleChapter(chapterKey)}
                      >
                        <span className="course-tree-chevron">
                          {collapsedChapters[chapterKey] ? "▶" : "▼"}
                        </span>
                        <span className="course-tree-chapter-label">
                          {chapterData.topicNumber} — {chapterData.label}
                        </span>
                      </button>

                      {!collapsedChapters[chapterKey] && (
                        <div className="course-tree-lessons">
                          {chapterData.lessons.map((lesson) => {
                            const isActive =
                              editorView === "lesson" &&
                              selectedLesson &&
                              selectedLesson.sectionNumber === lesson.sectionNumber;
                            const isGenerating = regeneratingLessons.has(
                              lesson.sectionNumber
                            );
                            const draft = lessonDrafts[String(lesson.sectionNumber)];
                            const draftStatus = draft?.status;
                            const showUnsaved =
                              draftStatus === "unsaved" ||
                              (isActive && hasUnsavedChanges && !draftStatus);

                            return (
                              <button
                                key={lesson.sectionNumber}
                                ref={isActive ? activeLessonRef : null}
                                className={`course-tree-lesson-row ${isActive ? "active" : ""}`}
                                onClick={() => handleLessonClick(lesson)}
                                title={lesson.sectionTitle}
                              >
                                <span className="lesson-row-number">
                                  {lesson.sectionNumber}
                                </span>
                                <span className="lesson-row-title">
                                  {lesson.sectionTitle}
                                </span>
                                {(isGenerating || draftStatus || showUnsaved) && (
                                  <span className="lesson-row-badges">
                                    {isGenerating && (
                                      <span className="lesson-badge generating">
                                        ↻ Generating…
                                      </span>
                                    )}
                                    {draftStatus === "queued" && (
                                      <span className="lesson-badge queued">Queued</span>
                                    )}
                                    {draftStatus === "editing" && (
                                      <span className="lesson-badge editing">✦ Editing…</span>
                                    )}
                                    {draftStatus === "error" && (
                                      <span
                                        className="lesson-badge failed"
                                        title={draft.message || "Edit failed"}
                                      >
                                        Edit failed
                                      </span>
                                    )}
                                    {showUnsaved && (
                                      <span
                                        className="lesson-badge unsaved"
                                        title="Unsaved changes. Open this lesson and press Save to DB."
                                      >
                                        ● Unsaved
                                      </span>
                                    )}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {!loading && !error && allLessons.length === 0 && (
            <div className="course-overview-empty">
              No lessons found. Select a course first.
            </div>
          )}
        </div>

        <div className="course-overview-footer">
          <span className="course-overview-count">
            {allLessons.length} lesson{allLessons.length !== 1 ? "s" : ""}
            {unsavedCount > 0 && (
              <span className="course-overview-unsaved-count">· {unsavedCount} unsaved</span>
            )}
          </span>
          <button className="course-overview-refresh" onClick={fetchAllLessons}>
            ↺ Refresh
          </button>
        </div>
      </div>
    </>
  );
};

export default CourseOverviewPanel;
